import {
  DetailRequirement,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  ResourceDataType,
  SingularTaskTraitDetail,
} from '@cibo/core'
import { Markdown, NumbersOnlyTextField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import { IconButton, InputAdornment, Stack, Tooltip } from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { TaskDataModel } from '../../../models/TaskDataModel'

type NumberDataEntryFieldProps = {
  taskId: string
  traitConfig: EngagementTaskTrait<ResourceDataType>
  taskData: UseQueryResult<TaskDataModel, Error>
  detailRequirement?: DetailRequirement
  resourceId: string
  onChange: (detail: MultipleTaskTraitDetail | SingularTaskTraitDetail) => void
  immutable?: boolean
  traitDetailValue: any
}

export const NumberDataEntryField = ({
  detailRequirement,
  traitConfig,
  onChange,
  immutable,
  traitDetailValue,
}: NumberDataEntryFieldProps) => {
  const { t } = useTranslation(['@cibo/programs/NumberDataEntryField', '@cibo/programs/DataEntry'])
  const { engagement, taskData } = useProgramEngagementContext()

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  const helperText =
    engagement.data && engagement.data.replaceCopyTokens((params as any)?.helperTextMd)

  const [displayValue, setDisplayValue] = useState<string | null>()

  useEffect(() => {
    if (!displayValue) setDisplayValue(traitDetailValue)
  }, [traitDetailValue])

  const saveNewValue = useDebounceCallback((newValue: string | null) => {
    onChange({
      traitId: traitConfig.traitId,
      value: newValue,
      year: detailRequirement?.year,
    })
  }, 300)

  useEffect(() => {
    if (displayValue === null) {
      saveNewValue(displayValue)
    } else if (!!displayValue && displayValue !== traitDetailValue) {
      saveNewValue(displayValue)
    }
  }, [displayValue])

  return (
    <Stack spacing={1}>
      <NumbersOnlyTextField
        value={displayValue || ''}
        onChange={event => setDisplayValue(event.target.value)}
        canFloat={params?.display === 'float'}
        disabled={immutable}
        slotProps={{
          input: {
            endAdornment: (
              <Stack direction="row" alignItems="center">
                <InputAdornment position="end">
                  <Tooltip title={t('removeValue')}>
                    <span>
                      <IconButton
                        data-testid="remove-number-button"
                        onClick={() => setDisplayValue(null)}
                        disabled={immutable}
                      >
                        <ClearIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              </Stack>
            ),
          },
        }}
      />

      {!!helperText && (
        <Markdown typographyProps={{ variant: 'caption' }}>
          {engagement.data?.replaceCopyTokens(helperText)}
        </Markdown>
      )}
    </Stack>
  )
}
